import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { EditDataType } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import {
    clearProviderConfigDetail,
    createProviderConfigStructureFetch,
    getProviderConfigDetailFetch,
    getProviderConfigStructureFetch,
    updateProviderConfigDetailFetch,
    updateProviderConfigStructureFetch
} from 'src/resources/providerConfigDetail/reducer';
import {
    getProviderConfigRequestKey,
    ProviderConfigDetailUpdatePayloadType
} from 'src/types/providerConfigDetail';
import { addMessage } from 'src/resources/alertMessages/reducer';
import StructureItemBlock from '../SystemConfigDetailContainer/StructureItemBlock';
import TreeBlock from '../SystemConfigDetailContainer/TreeBlock';
import { SystemConfigStructureItemType } from 'src/types/systemConfigDetail';
import {
    activateApplicationInstallFetch,
    deactivateApplicationInstallFetch,
    getApplicationInstallFetch,
    installApplicationInstallFetch
} from '../../resources/applicationInstall/reducer';
import {
    clearProviderDetail,
    getProviderDetailFetch
} from 'src/resources/providerDetail/reducer';
import translate from '@translate';

export type OnChangeType = (
    strctureItem: SystemConfigStructureItemType,
    value: string | number | undefined,
    error: boolean
) => void;

export const EDIT_KEYS = ['USER_NOTIFICATIONS'];
export const EDIT_STRUCTURE_KEYS = ['ITEM_KEY', 'TYPE', 'DESCRIPTION'];

export const ID_KEY = 'ITEM_KEY';

const initData: EditDataType = {};

interface ProviderConfigDetailContainerProps {
    integrationConfig?: boolean;
}

const buildSavePayload = (
    idProvider: string,
    structureData: SystemConfigStructureItemType[],
    editData: EditDataType,
    system?: string
): ProviderConfigDetailUpdatePayloadType => ({
    idProvider,
    system,
    data: structureData
        .filter((item) => item[ID_KEY] && editData[item[ID_KEY]] !== undefined)
        .map((item) => ({
            ID_PROVIDER: idProvider,
            ITEM_KEY: item[ID_KEY] || '',
            VALUE: item[ID_KEY] ? editData[item[ID_KEY]] : null
        }))
});

const ProviderConfigDetailContainer: FC<ProviderConfigDetailContainerProps> = (props) => {
    const dispatch = useDispatch();

    const { idProvider } = useParams();

    const data = useSelector((state: RootStoreType) => state.providerConfigDetail.data);
    const structureData = useSelector((state: RootStoreType) => state.providerConfigDetail.structureData);
    const dataKey = useSelector((state: RootStoreType) => state.providerConfigDetail.dataKey);
    const structureKey = useSelector((state: RootStoreType) => state.providerConfigDetail.structureKey);
    const loading = useSelector((state: RootStoreType) => state.providerConfigDetail.loading);
    const structureLoading = useSelector((state: RootStoreType) => state.providerConfigDetail.structureLoading);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const configItemTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.configItemTypeCodes);
    const applicationInstallData = useSelector((state: RootStoreType) => state.applicationInstall.data);
    const provider = useSelector((state: RootStoreType) => state.providerDetail.data);
    const providerLoading = useSelector((state: RootStoreType) => state.providerDetail.loading);

    const [editData, setEditData] = useState<EditDataType>(initData);
    const [editStructureData, setEditStructureData] = useState<SystemConfigStructureItemType | undefined>(undefined);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const missingSystemNotifiedRef = useRef(false);

    const permissionUpdate = useCheckPermission('PROVIDER_CONFIG.UPDATE');
    const permissionUpdateStructure = useCheckPermission('PROVIDER_CONFIG_STRUCTURE.UPDATE');
    const permissionInstall = useCheckPermission('APPLICATION_INSTALL.INSTALL');
    const permissionActivate = useCheckPermission('APPLICATION_INSTALL.ACTIVATE');
    const permissionDeactivate = useCheckPermission('APPLICATION_INSTALL.DEACTIVATE');

    const backUrl = '/admin/providers/';

    const expectedKey = useMemo(() => {
        if (!idProvider) {
            return null;
        }
        if (props.integrationConfig) {
            if (!provider?.SYSTEM) {
                return null;
            }
            return getProviderConfigRequestKey(idProvider, provider.SYSTEM);
        }
        return getProviderConfigRequestKey(idProvider);
    }, [idProvider, props.integrationConfig, provider?.SYSTEM]);

    useEffect(() => {
        if (idProvider) {
            dispatch(getProviderDetailFetch(idProvider));
        }
        return () => {
            dispatch(clearProviderDetail());
            dispatch(clearProviderConfigDetail());
        };
    }, [dispatch, idProvider]);

    useEffect(() => {
        if (idProvider) {
            dispatch(
                setLocationParams({
                    name: translate('providerConfigDetail.title', {
                        idProvider,
                        eshopName: provider?.COMPANY_NAME || ''
                    }),
                    backUrl
                })
            );
        }
    }, [dispatch, idProvider, provider?.COMPANY_NAME]);

    useEffect(() => {
        missingSystemNotifiedRef.current = false;
    }, [idProvider, props.integrationConfig]);

    useEffect(() => {
        if (!idProvider) {
            return;
        }

        if (props.integrationConfig) {
            dispatch(getApplicationInstallFetch(idProvider));

            if (providerLoading) {
                return;
            }

            if (!provider) {
                return;
            }

            if (!provider.SYSTEM) {
                if (!missingSystemNotifiedRef.current) {
                    missingSystemNotifiedRef.current = true;
                    dispatch(
                        addMessage({
                            type: 'error',
                            title: translate('providerConfigDetail.message.missingSystem')
                        })
                    );
                }
                return;
            }

            dispatch(getProviderConfigDetailFetch({ idProvider, system: provider.SYSTEM }));
            dispatch(getProviderConfigStructureFetch({ idProvider, system: provider.SYSTEM }));
            return;
        }

        dispatch(getProviderConfigDetailFetch({ idProvider }));
        dispatch(getProviderConfigStructureFetch({ idProvider }));
    }, [dispatch, idProvider, props.integrationConfig, provider, providerLoading]);

    useEffect(() => {
        if (data) {
            const newEditData: EditDataType = {};
            data.forEach((item) => {
                newEditData[item[ID_KEY]] = item.VALUE;
            });
            setEditData(newEditData);
        }
    }, [data, idProvider]);

    const onChange: OnChangeType = (strctureItem, value) => {
        const itemKey = strctureItem[ID_KEY];
        if (!itemKey) {
            return;
        }

        const newData = { ...editData };
        if (!value && value !== 0) {
            delete newData[itemKey];
        } else {
            newData[itemKey] = String(value);
        }
        setEditData(newData);
    };

    const dispatchSave = (savePayload: ProviderConfigDetailUpdatePayloadType) => {
        dispatch(updateProviderConfigDetailFetch(savePayload));
    };

    const handleSave = () => {
        if (!structureData || !idProvider || !expectedKey) {
            return;
        }

        if (dataKey !== expectedKey || structureKey !== expectedKey) {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('providerConfigDetail.message.missingSystem')
                })
            );
            return;
        }

        if (props.integrationConfig && !provider?.SYSTEM) {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('providerConfigDetail.message.missingSystem')
                })
            );
            return;
        }

        const savePayload = buildSavePayload(
            idProvider,
            structureData,
            editData,
            props.integrationConfig ? provider?.SYSTEM : undefined
        );

        const existingKeys = new Set((data || []).map((item) => item[ID_KEY]));
        const payloadKeys = new Set(savePayload.data.map((item) => item.ITEM_KEY));
        const keysToDelete = Array.from(existingKeys).filter((key) => !payloadKeys.has(key));

        if (keysToDelete.length > 0) {
            setConfirmParams({
                title: translate('providerConfigDetail.confirm.deleteKeysTitle'),
                content: translate('providerConfigDetail.confirm.deleteKeysText', {
                    keys: keysToDelete.join('\n')
                }),
                action: () => {
                    dispatchSave(savePayload);
                }
            });
            return;
        }

        dispatchSave(savePayload);
    };

    const handleInstall = () => {
        dispatch(installApplicationInstallFetch(idProvider));
    };

    const handleActivate = () => {
        const currentActive = applicationInstallData?.active ?? false;
        const currentInTest = applicationInstallData?.inTest ?? false;

        setConfirmParams({
            title: translate('systemConfigDetail.action.activateApplication'),
            content: translate('systemConfigDetail.action.activateApplicationConfirm', {
                active: String(currentActive).toUpperCase(),
                inTest: String(currentInTest).toUpperCase()
            }),
            customContent: (dialogData, setData) => {
                const boolOptions = [
                    { value: true, label: 'TRUE' },
                    { value: false, label: 'FALSE' }
                ];

                if (dialogData.active === undefined) {
                    setData({ active: true, inTest: false, disabled: false });
                }

                const downloadStatus = dialogData.active
                    ? translate('systemConfigDetail.action.statusActive')
                    : translate('systemConfigDetail.action.statusInactive');
                const uploadStatus = !dialogData.inTest
                    ? translate('systemConfigDetail.action.statusActive')
                    : translate('systemConfigDetail.action.statusInactive');

                return (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            {translate('systemConfigDetail.action.activateApplicationLabel')}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                {translate('systemConfigDetail.action.activeLabel')}:
                            </label>
                            <select
                                value={String(dialogData.active)}
                                onChange={(e) => setData({ ...dialogData, active: e.target.value === 'true' })}
                                style={{ width: '120px', padding: '8px', fontSize: '14px' }}
                            >
                                {boolOptions.map((opt) => (
                                    <option key={String(opt.value)} value={String(opt.value)}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                {translate('systemConfigDetail.action.inTestLabel')}:
                            </label>
                            <select
                                value={String(dialogData.inTest)}
                                onChange={(e) => setData({ ...dialogData, inTest: e.target.value === 'true' })}
                                style={{ width: '120px', padding: '8px', fontSize: '14px' }}
                            >
                                {boolOptions.map((opt) => (
                                    <option key={String(opt.value)} value={String(opt.value)}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                minWidth: '350px'
                            }}
                        >
                            <div style={{ marginBottom: '8px' }}>
                                <strong>{translate('systemConfigDetail.action.downloadLabel')}:</strong> {downloadStatus}
                            </div>
                            <div>
                                <strong>{translate('systemConfigDetail.action.uploadLabel')}:</strong> {uploadStatus}
                            </div>
                        </div>
                    </>
                );
            },
            action: (dialogData) => {
                dispatch(
                    activateApplicationInstallFetch({
                        idProvider,
                        active: dialogData.active,
                        inTest: dialogData.inTest
                    })
                );
            }
        });
    };

    const handleDeactivate = () => {
        const currentActive = applicationInstallData?.active ?? false;
        const currentInTest = applicationInstallData?.inTest ?? false;

        setConfirmParams({
            title: translate('systemConfigDetail.action.deactivateApplication'),
            content: translate('systemConfigDetail.action.deactivateApplicationConfirm', {
                active: String(currentActive).toUpperCase(),
                inTest: String(currentInTest).toUpperCase()
            }),
            customContent: (dialogData, setData) => {
                const boolOptions = [
                    { value: true, label: 'TRUE' },
                    { value: false, label: 'FALSE' }
                ];

                if (dialogData.active === undefined) {
                    setData({ active: false, inTest: true, disabled: false });
                }

                const downloadStatus = dialogData.active
                    ? translate('systemConfigDetail.action.statusActive')
                    : translate('systemConfigDetail.action.statusInactive');
                const uploadStatus = !dialogData.inTest
                    ? translate('systemConfigDetail.action.statusActive')
                    : translate('systemConfigDetail.action.statusInactive');

                return (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            {translate('systemConfigDetail.action.activateApplicationLabel')}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                {translate('systemConfigDetail.action.activeLabel')}:
                            </label>
                            <select
                                value={String(dialogData.active)}
                                onChange={(e) => setData({ ...dialogData, active: e.target.value === 'true' })}
                                style={{ width: '120px', padding: '8px', fontSize: '14px' }}
                            >
                                {boolOptions.map((opt) => (
                                    <option key={String(opt.value)} value={String(opt.value)}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                {translate('systemConfigDetail.action.inTestLabel')}:
                            </label>
                            <select
                                value={String(dialogData.inTest)}
                                onChange={(e) => setData({ ...dialogData, inTest: e.target.value === 'true' })}
                                style={{ width: '120px', padding: '8px', fontSize: '14px' }}
                            >
                                {boolOptions.map((opt) => (
                                    <option key={String(opt.value)} value={String(opt.value)}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                minWidth: '350px'
                            }}
                        >
                            <div style={{ marginBottom: '8px' }}>
                                <strong>{translate('systemConfigDetail.action.downloadLabel')}:</strong> {downloadStatus}
                            </div>
                            <div>
                                <strong>{translate('systemConfigDetail.action.uploadLabel')}:</strong> {uploadStatus}
                            </div>
                        </div>
                    </>
                );
            },
            action: (dialogData) => {
                dispatch(
                    deactivateApplicationInstallFetch({
                        idProvider,
                        active: dialogData.active,
                        inTest: dialogData.inTest
                    })
                );
            }
        });
    };

    const handleSaveStructure = (structureItem: EditDataType) => {
        if (!idProvider) {
            return;
        }

        if (structureItem.ID) {
            dispatch(
                updateProviderConfigStructureFetch({
                    id: String(structureItem.ID),
                    data: structureItem,
                    idProvider,
                    system: props.integrationConfig ? provider?.SYSTEM : undefined
                })
            );
        }
        if (!structureData?.find((structureDataItem) => structureDataItem[ID_KEY] === structureItem[ID_KEY])) {
            dispatch(
                createProviderConfigStructureFetch({
                    structureItem,
                    idProvider,
                    system: props.integrationConfig ? provider?.SYSTEM : undefined
                })
            );
        } else {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('systemConfig.messages.keyAlreadyUsed')
                })
            );
        }
    };

    const handleSetEditStructure = (structureItem?: SystemConfigStructureItemType) => {
        setEditStructureData(structureItem);
    };

    const handleAddMessage = (message: { type: string; title: string }) => {
        dispatch(addMessage(message));
    };

    const isProviderReady = !props.integrationConfig || (!providerLoading && !!provider?.SYSTEM);

    const isDataReady =
        isProviderReady &&
        !!expectedKey &&
        dataKey === expectedKey &&
        structureKey === expectedKey &&
        !!data &&
        !!structureData &&
        !loading &&
        !structureLoading;

    if (!isDataReady) {
        return <Loading />;
    }

    return (
        <>
            <TopBlock
                permissionUpdate={permissionUpdate}
                permissionUpdateStructure={permissionUpdateStructure}
                handleSave={handleSave}
                editStructureData={editStructureData}
                handleNewStructureData={() => handleSetEditStructure()}
                handleInstall={handleInstall}
                handleActivate={handleActivate}
                handleDeactivate={handleDeactivate}
                permissionInstallApplication={permissionInstall}
                permissionActivateApplicationInstall={permissionActivate}
                permissionDeactivateApplicationInstall={permissionDeactivate}
                applicationInstallData={applicationInstallData}
                integrationConfig={props.integrationConfig}
            />

            {!props.integrationConfig && (
                <StructureItemBlock
                    editData={editStructureData}
                    permissionUpdateStructure={permissionUpdateStructure}
                    configItemTypeCodes={configItemTypeCodes}
                    handleSubmit={handleSaveStructure}
                    handleAddMessage={handleAddMessage}
                />
            )}

            <TreeBlock
                structureData={structureData}
                onChange={onChange}
                boolNumberCodes={boolNumberCodes}
                editData={editData}
                loading={!!loading}
                permissionUpdate={permissionUpdate}
                setEditStructure={handleSetEditStructure}
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    action={confirmParams.action}
                    onClose={(): void => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ProviderConfigDetailContainer;
